// src/components/customers/CustomerPickerDialog.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import * as customerServices from "../services/customer";
import CarSelectDialog from "./CarSelectDialog";

export default function CustomerPickerDialog({
  open,
  onClose,
  showCreateButton = true,
  onSuccess,
}) {
  const [search, setSearch] = useState(""); // ค้นหาชื่อ/ทะเบียนรถ
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  // ถ้าต้องการ filter เพิ่มเติมภายหลัง สามารถเปิดใช้ได้
  const [carType] = useState("");
  const [licenseFilter] = useState("");
  const [provinceFilter] = useState("");

  const [openCarSelectDialog, setOpenCarSelectDialog] = useState(false);

  const navigate = useNavigate();

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const res = await customerServices.getCustomers("SELLER");
      const data = res?.data?.data ?? [];
      setCustomers(data);
    } catch (e) {
      console.error("Error fetching customers:", e);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      // reset state เล็กน้อยเมื่อเปิดใหม่
      setSearch("");
      fetchCustomers();
    }
  }, [open]);

  // helpers
  const includesSafe = (a = "", b = "") =>
    String(a).toLowerCase().includes(String(b).toLowerCase());

  const matchCarType = (customer) => {
    if (!carType) return true;
    return (customer.cars || []).some((car) => car?.type === carType);
  };

  const matchLicense = (customer) => {
    if (!licenseFilter) return true;
    return (customer.cars || []).some((car) =>
      includesSafe(car?.license, licenseFilter)
    );
  };

  const matchProvince = (customer) => {
    if (!provinceFilter) return true;
    return (customer.cars || []).some((car) =>
      includesSafe(car?.province, provinceFilter)
    );
  };

  const matchSearch = (customer) => {
    if (!search) return true;
    const byName = includesSafe(customer.name, search);
    const byCarLicense = (customer.cars || []).some((car) =>
      includesSafe(car?.license, search)
    );
    return byName || byCarLicense;
  };

  const filteredData = useMemo(
    () =>
      customers.filter(
        (c) =>
          matchSearch(c) &&
          matchCarType(c) &&
          matchLicense(c) &&
          matchProvince(c)
      ),
    [customers, search, carType, licenseFilter, provinceFilter]
  );

  const handleSelect = async (customer) => {
    console.log("customer", customer);
    setSelectedCustomer(customer);
    setOpenCarSelectDialog(true);
  };

  const handleCreateNew = () => {
    onClose?.();
    navigate(`/customers/create`);
  };

  const handleCustomerPicked = (customer) => {
    onSuccess && onSuccess(customer);
    onClose?.();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xxl">
        <DialogTitle sx={{ pb: 1 }}>เลือกซัพพลายเออร์</DialogTitle>

        <DialogContent dividers>
          {/* แถบค้นหา + ปุ่มสร้าง */}
          <Box
            sx={{
              mb: 2,
              display: "flex",
              gap: 2,
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <TextField
              fullWidth
              label="ค้นหา"
              placeholder="ค้นหาชื่อ หรือ ทะเบียนรถ"
              variant="outlined"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ mb: 2, mt: 2, width: 500 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {showCreateButton && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleCreateNew}
                sx={{ whiteSpace: "nowrap" }}
                size="large"
              >
                ซัพพลายเออร์ใหม่
              </Button>
            )}
          </Box>

          {/* รายการซัพพลายเออร์ */}
          {loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 2 }}
            >
              <CircularProgress />
            </Box>
          ) : filteredData.length === 0 ? (
            <Box sx={{ textAlign: "center", color: "text.secondary", py: 6 }}>
              <Typography>ไม่พบซัพพลายเออร์ที่ตรงกับคำค้นหา</Typography>
              <Typography>ลองเปลี่ยนคำค้น หรือเพิ่มซัพพลายเออร์ใหม่ได้เลย</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredData.map((item, index) => (
                <Grid item xs={12} md={6} lg={3} key={index}>
                  <Card
                    variant="outlined"
                    onClick={() => handleSelect(item)}
                    sx={{
                      cursor: "pointer",
                      height: "100%",
                      "&:hover": { boxShadow: 3 },
                    }}
                  >
                    <CardContent>
                      <Typography fontWeight="bold">{item.name}</Typography>
                      <Typography color="text.secondary">
                        {item.personType === "normal"
                          ? "บุคคลธรรมดา"
                          : "นิติบุคคล"}
                      </Typography>

                      {item.cars && item.cars.length > 0 && (
                        <Box
                          sx={{
                            mt: 1,
                            p: 1,
                            borderRadius: 1.5,
                            bgcolor: "action.hover",
                          }}
                        >
                          <Typography fontWeight="bold">
                            รถที่ลงทะเบียนไว้
                          </Typography>
                          {item.cars.map((car, idx) => (
                            <Typography
                              key={idx}
                              variant="body2"
                              color="text.secondary"
                            >
                              {car?.type ? `${car.type} - ` : ""}
                              {car?.license || "-"}
                              {car?.province ? ` (${car.province})` : ""}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} size="large">
            ปิดหน้าต่าง
          </Button>
        </DialogActions>
      </Dialog>

      <CarSelectDialog
        open={openCarSelectDialog}
        onClose={() => setOpenCarSelectDialog(false)}
        customerData={selectedCustomer}
        fetchData={fetchCustomers}
        onSuccess={handleCustomerPicked}
      />
    </>
  );
}
